<div class="p-8 min-h-screen bg-white m-4 rounded-xl border border-gray-100 shadow-sm">
  <!-- Header -->
  <div class="mb-6">
    <div class="flex items-center justify-between mb-4">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 mb-1">Users Management</h1>
        <p class="text-sm text-gray-600">View and manage all system users</p>
      </div>
      <div class="flex items-center gap-3">
        <!-- Create User Button -->
        <button 
          (click)="openCreateModal()"
          class="px-4 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:opacity-90 transition-colors flex items-center gap-2"
        >
          <i class="fas fa-plus"></i>
          Create User
        </button>
        <!-- Refresh Button -->
        <button 
          (click)="loadUsers()"
          class="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors flex items-center gap-2"
          [disabled]="loading"
        >
          <i class="fas fa-sync-alt" [class.fa-spin]="loading"></i>
          Refresh
        </button>
      </div>
    </div>

    <!-- Filters and Search -->
    <div class="flex items-center gap-3 flex-wrap">
      <!-- Search -->
      <div class="relative flex-1 min-w-[200px]">
        <input 
          type="text" 
          [(ngModel)]="searchTerm"
          (keyup.enter)="onSearch()"
          placeholder="Search by email, name, or phone..." 
          class="pl-10 pr-4 py-2.5 w-full border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white"
        />
        <i class="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"></i>
      </div>
      <button
        (click)="onSearch()"
        class="px-4 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:opacity-90 transition-colors"
        [disabled]="loading"
      >
        Search
      </button>

      <!-- Role Filter -->
      <select
        [(ngModel)]="selectedRole"
        (change)="onFilterChange()"
        class="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white"
      >
        @for (role of filterRoles; track role.value) {
          <option [value]="role.value">{{ role.label }}</option>
        }
      </select>

      <!-- Status Filter -->
      <select
        [(ngModel)]="selectedStatus"
        (change)="onFilterChange()"
        class="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white"
      >
        <option value="">All Status</option>
        <option value="active">Active</option>
        <option value="inactive">Inactive</option>
      </select>

      <!-- Clear Filters -->
      <button
        (click)="clearFilters()"
        class="px-4 py-2.5 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
        [disabled]="loading"
      >
        <i class="fas fa-times mr-1"></i>
        Clear
      </button>
    </div>
  </div>

  <!-- Success Message -->
  @if (successMessage) {
    <div class="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
      <div class="flex items-center gap-2">
        <i class="fas fa-check-circle text-green-600"></i>
        <p class="text-green-800">{{ successMessage }}</p>
      </div>
    </div>
  }

  <!-- Loading State -->
  @if (loading) {
    <div class="flex items-center justify-center py-12">
      <div class="text-center">
        <i class="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
        <p class="text-gray-600">Loading users...</p>
      </div>
    </div>
  }

  <!-- Error State -->
  @if (error && !loading) {
    <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
      <div class="flex items-center gap-2">
        <i class="fas fa-exclamation-circle text-red-600"></i>
        <p class="text-red-800">{{ error }}</p>
      </div>
    </div>
  }

  <!-- Users Table -->
  @if (!loading && !error) {
    <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
    <div class="overflow-x-auto">
      <table class="w-full">
        <thead class="bg-gray-50 border-b border-gray-200">
          <tr>
            <th class="text-left py-4 px-6 text-sm font-semibold text-gray-700">Name</th>
            <th class="text-left py-4 px-6 text-sm font-semibold text-gray-700">Email</th>
            <th class="text-left py-4 px-6 text-sm font-semibold text-gray-700">Phone</th>
            <th class="text-left py-4 px-6 text-sm font-semibold text-gray-700">Role</th>
            <th class="text-left py-4 px-6 text-sm font-semibold text-gray-700">Status</th>
            <th class="text-left py-4 px-6 text-sm font-semibold text-gray-700">Temp Password</th>
            <th class="text-left py-4 px-6 text-sm font-semibold text-gray-700">Created At</th>
            <th class="text-left py-4 px-6 text-sm font-semibold text-gray-700">Last Updated</th>
            <th class="text-left py-4 px-6 text-sm font-semibold text-gray-700">Actions</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200">
          @if (users.length === 0 && !loading) {
            <tr class="hover:bg-gray-50">
              <td colspan="9" class="py-12 text-center text-gray-500">
                <i class="fas fa-users text-4xl mb-3 text-gray-300"></i>
                <p class="text-lg font-medium">No users found</p>
                @if (searchTerm || selectedRole || selectedStatus) {
                  <p class="text-sm">
                    Try adjusting your filters or search terms
                  </p>
                  <button
                    (click)="clearFilters()"
                    class="mt-2 px-4 py-2 text-sm text-primary hover:underline"
                  >
                    Clear all filters
                  </button>
                }
              </td>
            </tr>
          }
          @for (user of filteredUsers; track user.id) {
            <tr class="hover:bg-gray-50 transition-colors">
            <td class="py-4 px-6">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <i class="fas fa-user text-primary text-sm"></i>
                </div>
                <div>
                  <p class="text-sm font-medium text-gray-900">{{ user.fullName }}</p>
                  <p class="text-xs text-gray-500">ID: {{ user.id }}</p>
                </div>
              </div>
            </td>
            <td class="py-4 px-6">
              <p class="text-sm text-gray-900">{{ user.email }}</p>
            </td>
            <td class="py-4 px-6">
              <p class="text-sm text-gray-600">{{ user.phoneNumber || '-' }}</p>
            </td>
            <td class="py-4 px-6">
              @if (editingRoleUserId === user.id) {
                <div class="flex items-center gap-2">
                  <select
                    [(ngModel)]="selectedEditRoleId"
                    class="px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                    [disabled]="updatingRole"
                  >
                    @for (role of availableRoles; track role.id) {
                      <option [ngValue]="role.id">{{ role.label }}</option>
                    }
                  </select>
                  <button
                    (click)="saveRoleEdit(user)"
                    class="px-2 py-1 text-xs rounded bg-primary text-white hover:opacity-90 disabled:opacity-50"
                    [disabled]="updatingRole || selectedEditRoleId === null"
                    title="Save role"
                  >
                    <i class="fas" [ngClass]="updatingRole ? 'fa-spinner fa-spin' : 'fa-check'"></i>
                  </button>
                  <button
                    (click)="cancelRoleEdit()"
                    class="px-2 py-1 text-xs rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
                    [disabled]="updatingRole"
                    title="Cancel"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              } @else {
                <div class="flex items-center gap-2">
                  <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                        [ngClass]="{
                          'bg-purple-100 text-purple-800': user.roleId === 69,
                          'bg-blue-100 text-blue-800': user.roleId === 1,
                          'bg-indigo-100 text-indigo-800': user.roleId === 2,
                          'bg-orange-100 text-orange-800': user.roleId === 3,
                          'bg-gray-100 text-gray-800': user.roleId === 4
                        }">
                    {{ getRoleLabel(user.roleId) }}
                  </span>
                  @if (canEditRoles()) {
                    <button
                      (click)="startRoleEdit(user)"
                      class="text-primary hover:text-primary/80"
                      title="Edit role"
                    >
                      <i class="fas fa-pen text-xs"></i>
                    </button>
                  }
                </div>
              }
            </td>
            <td class="py-4 px-6">
              <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                    [ngClass]="{
                      'bg-green-100 text-green-800': user.isActive,
                      'bg-red-100 text-red-800': !user.isActive
                    }">
                <i class="fas fa-circle text-[6px] mr-1.5"></i>
                {{ user.isActive ? 'Active' : 'Inactive' }}
              </span>
            </td>
            <td class="py-4 px-6">
              @if (!hasUserChangedPassword(user) && getTemporaryPassword(user.id)) {
                <div class="flex items-center gap-2">
                  <code class="px-2 py-1 bg-yellow-50 border border-yellow-200 rounded text-xs font-mono text-yellow-800">
                    {{ getTemporaryPassword(user.id) }}
                  </code>
                  <button
                    (click)="copyToClipboard(getTemporaryPassword(user.id)!)"
                    class="text-yellow-600 hover:text-yellow-800"
                    title="Copy password"
                  >
                    <i class="fas fa-copy text-xs"></i>
                  </button>
                </div>
              } @else if (hasUserChangedPassword(user)) {
                <span class="text-xs text-gray-400 italic">
                  Password changed
                </span>
              } @else {
                <span class="text-xs text-gray-400 italic">
                  Not available
                </span>
              }
            </td>
            <td class="py-4 px-6">
              <p class="text-sm text-gray-600">{{ formatDate(user.createdAt) }}</p>
            </td>
            <td class="py-4 px-6">
              <p class="text-sm text-gray-600">{{ formatDate(user.updatedAt) }}</p>
            </td>
            <td class="py-4 px-6">
              <button
                (click)="toggleUserStatus(user)"
                class="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                [ngClass]="{
                  'bg-red-100 text-red-700 hover:bg-red-200': user.isActive,
                  'bg-green-100 text-green-700 hover:bg-green-200': !user.isActive
                }"
                [title]="user.isActive ? 'Deactivate user' : 'Activate user'"
              >
                <i class="fas" [ngClass]="user.isActive ? 'fa-ban' : 'fa-check'"></i>
                {{ user.isActive ? 'Deactivate' : 'Activate' }}
              </button>
            </td>
          </tr>
          }
        </tbody>
      </table>
    </div>

    <!-- Pagination Info and Controls -->
    <div class="bg-gray-50 border-t border-gray-200 px-6 py-4">
      <div class="flex items-center justify-between flex-wrap gap-4">
        <!-- Pagination Info -->
        <div class="text-sm text-gray-600">
          @if (totalCount > 0) {
            <p>
              Showing <span class="font-medium text-gray-900">{{ (pageNumber - 1) * pageSize + 1 }}</span> 
              to <span class="font-medium text-gray-900">{{ Math.min(pageNumber * pageSize, totalCount) }}</span> 
              of <span class="font-medium text-gray-900">{{ totalCount }}</span> users
              @if (totalPages > 1) {
                <span>(Page {{ pageNumber }} of {{ totalPages }})</span>
              }
            </p>
          } @else {
            <p class="text-gray-500">
              No users found
            </p>
          }
        </div>

        <!-- Pagination Controls -->
        @if (totalPages > 1) {
          <div class="flex items-center gap-2">
          <!-- Page Size Selector -->
          <div class="flex items-center gap-2">
            <label class="text-sm text-gray-600">Per page:</label>
            <select
              [(ngModel)]="pageSize"
              (change)="onPageSizeChange()"
              class="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white"
            >
              <option [value]="10">10</option>
              <option [value]="25">25</option>
              <option [value]="50">50</option>
              <option [value]="100">100</option>
            </select>
          </div>

          <!-- Previous Button -->
          <button
            (click)="previousPage()"
            [disabled]="!hasPreviousPage || loading"
            class="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i class="fas fa-chevron-left"></i>
          </button>

          <!-- Page Numbers -->
          <div class="flex items-center gap-1">
            @for (page of getPageNumbers(); track page) {
              <button
                (click)="goToPage(page)"
                [class.bg-primary]="page === pageNumber"
                [class.text-white]="page === pageNumber"
                [class.text-gray-700]="page !== pageNumber"
                class="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                [disabled]="loading"
              >
                {{ page }}
              </button>
            }
          </div>

          <!-- Next Button -->
          <button
            (click)="nextPage()"
            [disabled]="!hasNextPage || loading"
            class="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i class="fas fa-chevron-right"></i>
          </button>
          </div>
        }
      </div>
    </div>
    </div>
  }
</div>

<!-- Create User Modal -->
@if (showCreateModal) {
  <div class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
  <!-- Background overlay -->
  <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" (click)="closeCreateModal()"></div>

  <!-- Modal container -->
  <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
    <div class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg" (click)="$event.stopPropagation()">
      <!-- Modal header -->
      <div class="bg-white px-6 py-4 border-b border-gray-200">
        <div class="flex items-center justify-between">
          <h3 class="text-lg font-semibold text-gray-900" id="modal-title">Create New User</h3>
          <button
            type="button"
            (click)="closeCreateModal()"
            class="text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
        <p class="mt-1 text-sm text-gray-500">A temporary password will be generated automatically.</p>
      </div>

      <!-- Modal body -->
      <form [formGroup]="createUserForm" (ngSubmit)="onCreateUser()" class="px-6 py-4">
        <!-- Error message -->
        @if (error) {
          <div class="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
            <div class="flex items-center gap-2">
              <i class="fas fa-exclamation-circle text-red-600"></i>
              <p class="text-sm text-red-800">{{ error }}</p>
            </div>
          </div>
        }

        <!-- Success message -->
        @if (successMessage) {
          <div class="mb-4 bg-green-50 border border-green-200 rounded-lg p-3">
            <div class="flex items-center gap-2">
              <i class="fas fa-check-circle text-green-600"></i>
              <p class="text-sm text-green-800">{{ successMessage }}</p>
            </div>
          </div>
        }

        <!-- Full Name -->
        <div class="mb-4">
          <label for="fullName" class="block text-sm font-medium text-gray-700 mb-1">
            Full Name <span class="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="fullName"
            formControlName="fullName"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            [class.border-red-500]="createUserForm.get('fullName')?.invalid && createUserForm.get('fullName')?.touched"
            placeholder="Enter full name"
          />
          @if (createUserForm.get('fullName')?.invalid && createUserForm.get('fullName')?.touched) {
            <p class="mt-1 text-xs text-red-600">
              Full name is required (minimum 2 characters)
            </p>
          }
        </div>

        <!-- Email -->
        <div class="mb-4">
          <label for="email" class="block text-sm font-medium text-gray-700 mb-1">
            Email <span class="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            formControlName="email"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            [class.border-red-500]="createUserForm.get('email')?.invalid && createUserForm.get('email')?.touched"
            placeholder="Enter email address"
          />
          @if (createUserForm.get('email')?.invalid && createUserForm.get('email')?.touched) {
            <p class="mt-1 text-xs text-red-600">
              Please enter a valid email address
            </p>
          }
        </div>

        <!-- Phone Number -->
        <div class="mb-4">
          <label for="phoneNumber" class="block text-sm font-medium text-gray-700 mb-1">
            Phone Number
          </label>
          <input
            type="tel"
            id="phoneNumber"
            formControlName="phoneNumber"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Enter phone number (optional)"
          />
        </div>

        <!-- Role -->
        <div class="mb-4">
          <label for="role" class="block text-sm font-medium text-gray-700 mb-1">
            Role <span class="text-red-500">*</span>
          </label>
          <select
            id="role"
            formControlName="role"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white"
            [class.border-red-500]="createUserForm.get('role')?.invalid && createUserForm.get('role')?.touched"
          >
            <option value="">Select a role</option>
            @for (role of availableRoles; track role.id) {
              <option [value]="role.id">
                {{ role.label }}
              </option>
            }
          </select>
          @if (createUserForm.get('role')?.invalid && createUserForm.get('role')?.touched) {
            <p class="mt-1 text-xs text-red-600">
              Please select a role
            </p>
          }
          @if (availableRoles.length > 0) {
            <p class="mt-1 text-xs text-gray-500">
              <i class="fas fa-info-circle mr-1"></i>
              You can create users with the following roles: {{ getAvailableRolesText() }}
            </p>
          }
        </div>

        <!-- Info about temporary password -->
        <div class="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div class="flex items-start gap-2">
            <i class="fas fa-info-circle text-blue-600 mt-0.5"></i>
            <div class="text-sm text-blue-800">
              <p class="font-medium mb-1">Temporary Password</p>
              <p>A secure temporary password will be automatically generated and displayed after user creation. The user will be required to change it on their first login.</p>
            </div>
          </div>
        </div>

        <!-- Modal footer -->
        <div class="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            (click)="closeCreateModal()"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
            [disabled]="submitting"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
            [disabled]="submitting || createUserForm.invalid"
          >
            @if (!submitting) {
              <span>Create User</span>
            } @else {
              <span class="flex items-center gap-2">
                <i class="fas fa-spinner fa-spin"></i>
                Creating...
              </span>
            }
          </button>
        </div>
      </form>
    </div>
  </div>
  </div>
  }

